import { DatePipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { ExportFormat } from '../../../../core/buttons/export-button.component';
import { TagComponent, TagType } from '../../../../core/labels/tag.component';
import { DetailItemComponent } from '../../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../../core/table/column.component';
import { TableComponent } from '../../../../core/table/table.component';
import { ExportService } from '../../../../../services/export/export.service';
import { AbsenceExportMapper } from '../../../../../utilities/absence-export-mapper';

@Component({
  selector: 'app-absence-information',
  imports: [
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
  ],
  providers: [DatePipe],
  template: `<div class="tw-italic tw-text-md tw-pb-3">
      The Absence section displays all of the leave claims/absences for the
      employee.
    </div>

    <app-table
      [dataSource]="dataSource"
      [pageSize]="5"
      recordLabel="Absence"
      [showExport]="true"
      [expandable]="true"
      dataKey="absenceNumber"
      [globalFilterFields]="[
        'absenceNumber',
        'dateBegin',
        'dateEnd',
        'activeStatus',
        'absenceStatus',
        'absenceType',
      ]"
      (export)="onExport($event)"
    >
      <app-column name="absenceNumber" label="Claim Number" width="15%" />
      <app-column name="dateBegin" label="Date Begin" width="15%" format="d" />
      <app-column name="dateEnd" label="Date End" width="15%" format="d" />
      <app-column name="activeStatus" label="Status" width="25%">
        <ng-template #value let-data>
          <app-tag
            [type]="getStatusTagType(data.activeStatus, data.absenceStatus)"
          >
            {{ data.activeStatus }} - {{ data.absenceStatus }}
          </app-tag>
        </ng-template>
      </app-column>

      <app-column name="absenceType" label="Type" width="25%" />

      <ng-template #expand let-data>
        <div class="tw-flex tw-gap-8">
          <div class="tw-basis-1/3">
            <app-detail-panel [dataSource]="data">
              <app-detail-item label="Hours Missed" name="hoursMissed" />
              <app-detail-item
                label="Actual RTW date"
                name="dateRTW"
                labelCase="none"
                format="d"
              />
              <app-detail-item
                label="Estimated RTW date"
                name="estDateRTW"
                labelCase="none"
                format="d"
              />
            </app-detail-panel>
          </div>

          <div class="tw-basis-1/3">
            <app-detail-panel [dataSource]="data">
              <app-detail-item
                label="Approved From"
                name="dateApprovedFrom"
                format="d"
              />
              <app-detail-item
                label="Approved To"
                name="dateApprovedTo"
                format="d"
              />
              <app-detail-item label="Hours Approved" name="hoursApproved" />
            </app-detail-panel>
          </div>
          <div class="tw-basis-1/3">
            <app-detail-panel [dataSource]="data">
              <app-detail-item
                label="Date Reported"
                name="dateReported"
                format="d"
              />
              <app-detail-item label="Reported By" name="reportedBy" />
              <app-detail-item
                label="Hours Worked In The Last 12 Months"
                name="hoursWorked"
              />
            </app-detail-panel>
          </div>
        </div>
      </ng-template>
    </app-table>`,
  styles: [],
})
export class AbsenceInformationComponent {
  private datePipe = inject(DatePipe);
  private exportService = inject(ExportService);

  @Input()
  dataSource: any[] = [];

  getStatusTagType(
    activeStatus: string | null | undefined,
    absenceStatus: string | null | undefined,
  ): TagType {
    const absence = (absenceStatus || '').trim().toLowerCase();

    if (absence.includes('denied')) return 'Error';
    if (absence.includes('approved')) return 'Success';
    if (absence.includes('pending') || absence.includes('conditional'))
      return 'Warn';

    const normalized = (activeStatus || '').trim().toLowerCase();
    if (!normalized) return 'Grey';
    if (normalized.startsWith('open') || normalized.startsWith('reopen'))
      return 'Success';
    if (normalized.startsWith('closed')) return 'Error';
    if (normalized.startsWith('incident')) return 'Warn';

    return 'Grey';
  }

  async onExport(format: ExportFormat) {
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Absence_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = AbsenceExportMapper.toCsvExport(
        this.dataSource,
        'Absence',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = AbsenceExportMapper.toExcelExport(
        this.dataSource,
        'Absence',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
